// src/pages/PaperMacheShaping.js
import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
} from "react";
import * as THREE from "three";
import { STLLoader } from "three/examples/jsm/loaders/STLLoader";
import { sliceModelXZ, sliceModelYZ } from "../utils/sliceModel";
import { initializeThreeScene } from "../utils/initializeThreeScene";
import useVisualHelpers from "../hooks/useVisualHelpers";
import TutorialWindow from "../components/TutorialWindow";
import { paperMacheTutorial } from "../utils/tutorialContents";
import { useSyncedCamera } from "../hooks/useSyncedCamera";
import { downloadSVG } from "../utils/exportUtils";
import { Brush, Evaluator, ADDITION, SUBTRACTION } from "three-bvh-csg";
import "../styles/common.css";

const LASER_CUTTER_PRESETS = {
  'Glowforge Basic': { width: 279, height: 495 },
  'Epilog Zing 16': { width: 406, height: 305 },
  'Trotec Speedy 400': { width: 1016, height: 610 },
  'Custom': { width: 10, height: 10 }
};

// ===================== 槽位与曲线工具（与 clay 一致） =====================

// 由线段索引（i1,i2）拼成闭合曲线（世界坐标）
const buildClosedCurveWorld = (segments, uniqueVertices, matrixWorld) => {
  const worldVertices = uniqueVertices.map((v) => v.clone().applyMatrix4(matrixWorld));
  const used = new Set();
  const curve = [];
  let current = segments[0].i1;
  curve.push(worldVertices[current]);
  let found = true;
  let safety = 0;
  while (found && safety < segments.length * 2) {
    found = false;
    for (let i = 0; i < segments.length; i++) {
      if (used.has(i)) continue;
      const s = segments[i];
      if (s.i1 === current) {
        current = s.i2;
        curve.push(worldVertices[current]);
        used.add(i);
        found = true;
        break;
      } else if (s.i2 === current) {
        current = s.i1;
        curve.push(worldVertices[current]);
        used.add(i);
        found = true;
        break;
      }
    }
    safety++;
  }
  if (!curve[0].equals(curve[curve.length - 1])) curve.push(curve[0].clone());
  return curve;
};

// 计算水平/垂直片的相交线，并在两类片上生成对应的槽（与 Interlocking 同步）
const calculateIntersections = (horizontalContours, verticalContours, materialThickness) => {
  const result = { lines: [], slots: { horizontal: [], vertical: [] } };
  horizontalContours.forEach((hContour, hIndex) => {
    verticalContours.forEach((vContour, vIndex) => {
      const hY = hContour.y;
      const vX = vContour.x;
      const verticalPoints = vContour.points;
      const yCoords = verticalPoints.map((p) => p.x);
      const minY = Math.min(...yCoords);
      const maxY = Math.max(...yCoords);
      if (minY <= hY && hY <= maxY) {
        const projectedPoints = [];
        for (let i = 0; i < verticalPoints.length; i++) {
          const curr = verticalPoints[i];
          const next = verticalPoints[(i + 1) % verticalPoints.length];
          if ((curr.x - hY) * (next.x - hY) > 0) continue;
          const t = (hY - curr.x) / (next.x - curr.x);
          const z = curr.y + t * (next.y - curr.y);
          projectedPoints.push(new THREE.Vector2(vX, z));
        }
        if (projectedPoints.length >= 2) {
          projectedPoints.sort((a, b) => a.y - b.y);
          const firstPoint = projectedPoints[0];
          const lastPoint = projectedPoints.at(-1);
          const direction = new THREE.Vector2().subVectors(lastPoint, firstPoint).normalize();
          const offset = direction.clone().multiplyScalar(materialThickness * 0.5);
          const start = new THREE.Vector2().copy(firstPoint).sub(offset);
          const end = new THREE.Vector2().copy(lastPoint).add(offset);

          const lineStart = new THREE.Vector3(vX, hY, start.y);
          const lineEnd = new THREE.Vector3(vX, hY, end.y);
          result.lines.push({ start: lineStart, end: lineEnd, hIndex, vIndex });

          const mid = new THREE.Vector3().addVectors(lineStart, lineEnd).multiplyScalar(0.5);
          // 跟 Interlocking 的定义一致：水平片槽朝 Z，垂直片槽朝 Y
          result.slots.horizontal.push({
            center: new THREE.Vector3().addVectors(lineStart, mid).multiplyScalar(0.5),
            length: lineStart.distanceTo(mid),
            width: materialThickness * 1.1,
            height: materialThickness * 1.1,
            direction: new THREE.Vector3(0, 0, 1),
            hIndex,
            vIndex,
          });
          result.slots.vertical.push({
            center: new THREE.Vector3().addVectors(mid, lineEnd).multiplyScalar(0.5),
            length: mid.distanceTo(lineEnd),
            width: materialThickness * 1.1,
            height: materialThickness * 1.1,
            direction: new THREE.Vector3(0, 1, 0),
            hIndex,
            vIndex,
          });
        }
      }
    });
  });
  return result;
};

const applySlotsToParts = (horizontalPlanes, verticalPlanes, slots) => {
  const evaluator = new Evaluator();

  const apply = (planes, slotList, isVertical) => {
    planes.forEach((plane, index) => {
      const planeSlots = slotList.filter((s) => (isVertical ? s.vIndex : s.hIndex) === index);
      if (!planeSlots.length) return;

      const planeBrush = new Brush(plane.geometry);
      planeBrush.position.copy(plane.position);
      planeBrush.rotation.copy(plane.rotation);
      planeBrush.scale.copy(plane.scale);
      planeBrush.updateMatrixWorld(true);

      const slotBrushes = planeSlots.map((slot) => {
        const box = new THREE.BoxGeometry(
          isVertical ? slot.width : slot.width,
          isVertical ? slot.length : slot.height,
          isVertical ? slot.height : slot.length
        );
        const b = new Brush(box);
        b.position.copy(slot.center);
        b.lookAt(new THREE.Vector3().addVectors(slot.center, slot.direction));
        b.updateMatrixWorld(true);
        return b;
      });

      let combined = slotBrushes[0];
      for (let i = 1; i < slotBrushes.length; i++) {
        combined = evaluator.evaluate(combined, slotBrushes[i], ADDITION);
      }

      const resultEval = evaluator.evaluate(planeBrush, combined, SUBTRACTION);
      const inv = new THREE.Matrix4().copy(plane.matrixWorld).invert();
      resultEval.geometry.applyMatrix4(inv);

      plane.geometry.dispose();
      plane.geometry = resultEval.geometry;
    });
  };

  apply(horizontalPlanes, slots.horizontal, false);
  apply(verticalPlanes, slots.vertical, true);
};

// ===================== 组件 =====================

const PaperMacheShaping = ({ stlFile }) => {
  const [showControlPanel, setShowControlPanel] = useState(false);
  const [tutorialVisible, setTutorialVisible] = useState(false);
  const [cutNumber, setCutNumber] = useState(6);
  // const [materialThickness, setMaterialThickness] = useState(3);
  const [svgReady, setSvgReady] = useState(false);
  const [constraintWarnings, setConstraintWarnings] = useState([]);

  const containerRef = useRef(null);
  const sceneRef = useRef(null);
  const rendererRef = useRef(null);
  const cameraRef = useRef(null);
  const controlsRef = useRef(null);

  const modelRef = useRef(null);
  const layersGroupRef = useRef(null);
  const shellsGroupRef = useRef(null); // 如果不需要外壳可不加入 scene
  const verticalPlanesRef = useRef([]);
  const horizontalPlanesRef = useRef([]);
  const verticalContoursRef = useRef([]);
  const horizontalContoursRef = useRef([]);
  // Paper-mâché is slow and hands-on → not ideal for rapid iteration
  const slowFabrication = useMemo(() => true, []);
  // ⬇️ put these near the top of the component body, before any usage
  const getBaseThickness = () => {
    const b = window.labConstraints?.materialBaseThickness ?? 3;
    // clamp to [1, 12]
    return Math.min(12, Math.max(1, Number(b) || 3));
  };

  // define the state FIRST (nothing above should reference materialThickness)
  const [materialThickness, setMaterialThickness] = React.useState(() => getBaseThickness());

  // derived slider props (these can come immediately after)
  const baseT = getBaseThickness();          // current base thickness from Sidebar
  const thicknessMin = baseT;                // slider min
  const thicknessMax = 12;                   // slider max
  const thicknessStep = baseT;               // snap step = base

  // react to Sidebar updates and re-snap the value
  React.useEffect(() => {
    const onLabUpdated = () => {
      const b = getBaseThickness();
      setMaterialThickness(prev => {
        const snapped = Math.round((Number(prev) || b) / b) * b;
        return Math.min(12, Math.max(b, snapped));
      });
    };
    window.addEventListener("labConstraints:updated", onLabUpdated);
    return () => window.removeEventListener("labConstraints:updated", onLabUpdated);
  }, []);
  const pageId = useMemo(() => `paper-mache`, []);
  useSyncedCamera(pageId, cameraRef, controlsRef);

  const { createVisualization } = useVisualHelpers(); // 可选，用于包围盒等可视化

  // 纸浆质感材质（UI 保持 paper 风格）
  const paperMaterial = useMemo(
    () =>
      new THREE.MeshStandardMaterial({
        color: 0xd7ccc8,
        roughness: 0.95,
        metalness: 0.0,
        side: THREE.FrontSide,     // ✅ 单面，避免双面排序抖动
        transparent: false,        // ✅ 取消透明，避免半透明排序闪烁
        depthWrite: true,          // ✅ 写深度，稳定遮挡关系
        polygonOffset: true,       // ✅ 跟地面/基模拉开
        polygonOffsetFactor: 2,
        polygonOffsetUnits: 2,
      }),
    []
  );

  // 清理/重建分组
  const clearGroups = () => {
    const dispose = (g) => {
      if (!g) return;
      g.traverse((o) => {
        if (o.geometry) o.geometry.dispose();
        if (o.material) o.material.dispose?.();
      });
      sceneRef.current.remove(g);
    };
    dispose(layersGroupRef.current);
    dispose(shellsGroupRef.current);
    layersGroupRef.current = new THREE.Group();
    shellsGroupRef.current = new THREE.Group();
    sceneRef.current.add(layersGroupRef.current);
    // 如需半透明“包裹壳”效果，可把 shellsGroupRef.current 也加入场景
    // sceneRef.current.add(shellsGroupRef.current);

    verticalPlanesRef.current = [];
    horizontalPlanesRef.current = [];
    verticalContoursRef.current = [];
    horizontalContoursRef.current = [];
  };

// 在 PaperMacheShaping.js 中找到并修改：

const checkModelBBoxConstraints = useCallback(() => {
  if (!modelRef.current) return true;
  const cutter = LASER_CUTTER_PRESETS["Trotec Speedy 400"]; // 修改这里
  const bedMin = Math.min(cutter.width, cutter.height);

  const bbox = new THREE.Box3().setFromObject(modelRef.current);
  const size = bbox.getSize(new THREE.Vector3());
  const modelMax = Math.max(size.x, size.y, size.z);

  const warns = [];
  if (modelMax > bedMin) {
    warns.push({
      type: "overall_fit",
      plane: "overall",
      layer: "ALL",
      currentSize: { width: modelMax.toFixed(1), height: modelMax.toFixed(1) },
      maxSize: { width: bedMin.toFixed(1), height: bedMin.toFixed(1) },
      cutter: "Trotec Speedy 400", // 修改这里
    });
  }
  setConstraintWarnings(warns);
  return warns.length === 0;
}, []);

  // 生成切片与槽位（几何核心，等同 clay 流程）
  const generateSlicesAndSlots = useCallback(() => {
    if (!modelRef.current || !sceneRef.current) return;
    clearGroups();

    const model = modelRef.current;
    model.updateMatrixWorld(true);
    const bbox = new THREE.Box3().setFromObject(model);

    const verticalX = Array.from({ length: cutNumber + 2 }, (_, i) =>
      bbox.min.x + (i / (cutNumber + 1)) * (bbox.max.x - bbox.min.x)
    );
    const horizontalY = Array.from({ length: cutNumber + 2 }, (_, i) =>
      bbox.min.y + (i / (cutNumber + 1)) * (bbox.max.y - bbox.min.y)
    );

    // 垂直片（YZ）
    verticalX.forEach((x) => {
      const res = sliceModelYZ(model, null, x);
      if (!res?.componentSegments) return;
      res.componentSegments.forEach((segs) => {
        if (segs.length < 2) return;
        const pts = buildClosedCurveWorld(segs, res.uniqueVertices, model.matrixWorld);
        if (pts.length < 3) return;

        const shape = new THREE.Shape(pts.map((p) => new THREE.Vector2(p.y, p.z)));
        const geom = new THREE.ExtrudeGeometry(shape, {
          depth: materialThickness,
          bevelEnabled: false,
        });
        const mesh = new THREE.Mesh(geom, paperMaterial);
        mesh.rotation.y = Math.PI / 2;
        mesh.rotation.x = Math.PI / 2;
        mesh.position.x = x - materialThickness / 2;
        mesh.updateMatrixWorld(true);

        layersGroupRef.current.add(mesh);
        verticalPlanesRef.current.push(mesh);
        verticalContoursRef.current.push({ x, points: shape.getPoints() });
      });
    });

    // 水平片（XZ）
    horizontalY.forEach((y) => {
      const res = sliceModelXZ(model, null, y);
      if (!res?.componentSegments) return;
      res.componentSegments.forEach((segs) => {
        if (segs.length < 2) return;
        const pts = buildClosedCurveWorld(segs, res.uniqueVertices, model.matrixWorld);
        if (pts.length < 3) return;

        const shape = new THREE.Shape(pts.map((p) => new THREE.Vector2(p.x, p.z)));
        const geom = new THREE.ExtrudeGeometry(shape, {
          depth: materialThickness,
          bevelEnabled: false,
        });
        const mesh = new THREE.Mesh(geom, paperMaterial);
        mesh.rotation.x = Math.PI / 2;
        mesh.position.y = y + materialThickness / 2;
        mesh.updateMatrixWorld(true);

        layersGroupRef.current.add(mesh);
        horizontalPlanesRef.current.push(mesh);
        horizontalContoursRef.current.push({ y, points: shape.getPoints() });
      });
    });

    // 槽位计算 + 减槽
    const { slots } = calculateIntersections(
      horizontalContoursRef.current,
      verticalContoursRef.current,
      materialThickness
    );
    applySlotsToParts(horizontalPlanesRef.current, verticalPlanesRef.current, slots);

    setSvgReady(true);
    setTimeout(() => checkModelBBoxConstraints(), 0);

  }, [cutNumber, materialThickness, paperMaterial]);

  // 下载 SVG（与 clay 相同，合并两组轮廓）
  const handleDownloadSVG = useCallback(() => {
    const combined = [
      ...horizontalContoursRef.current.map((c) => ({ ...c, plane: "horizontal" })),
      ...verticalContoursRef.current.map((c) => ({ ...c, plane: "vertical" })),
    ];
    if (!combined.length) return;
    downloadSVG(combined);
  }, []);

  // 灯光/地面/坐标轴等
  const initHelpers = useCallback(() => {
    if (!sceneRef.current) return;
    const gridHelper = new THREE.GridHelper(200, 20);
    gridHelper.position.y = 0;
    sceneRef.current.add(gridHelper);

    const axesHelper = new THREE.AxesHelper(100);
    sceneRef.current.add(axesHelper);

    const dirLight = new THREE.DirectionalLight(0xffffff, 1);
    dirLight.position.set(5, 10, 7.5).multiplyScalar(2);
    dirLight.castShadow = true;
    sceneRef.current.add(dirLight);

    const ambient = new THREE.HemisphereLight(0xffffff, 0xbfd4d2, 0.9);
    sceneRef.current.add(ambient);

    const shadowPlane = new THREE.Mesh(
      new THREE.PlaneGeometry(1000, 1000),
      new THREE.ShadowMaterial({ transparent: true, opacity: 0.1, side: THREE.DoubleSide })
    );
    shadowPlane.rotation.x = -Math.PI / 2;
    shadowPlane.receiveShadow = true;
    sceneRef.current.add(shadowPlane);
  }, []);

  // 载入模型（与 clay 同步的居中/落地方式）
  const loadModel = useCallback(() => {
    const loader = new STLLoader();
    const path = stlFile || "/models/twistT.stl";
    loader.load(
      path,
      (geometry) => {
        geometry.computeBoundingBox();
        const c = geometry.boundingBox.getCenter(new THREE.Vector3());
        geometry.translate(-c.x, -c.y, -c.z);
        // 如需与 clay 完全同坐标，可解开旋转：
        geometry.rotateX(-Math.PI / 2);
         geometry.rotateY(-Math.PI / 2);

        const mesh = new THREE.Mesh(
          geometry,
          new THREE.MeshStandardMaterial({ color: 0xffffff, opacity: 0.5, transparent: true })
        );
        mesh.updateMatrixWorld(true);
       sceneRef.current.add(mesh);

        const bbox = new THREE.Box3().setFromObject(mesh);
        mesh.position.y -= bbox.min.y; // 落地
        mesh.updateMatrixWorld(true);

        if (modelRef.current && sceneRef.current) sceneRef.current.remove(modelRef.current);
        modelRef.current = mesh;

        // 可选：包围盒/辅助可视化
        // createVisualization(mesh, sceneRef.current, bbox);

        checkModelBBoxConstraints();
        setTimeout(() => generateSlicesAndSlots(), 30);
      },
      undefined,
      (e) => console.error("Error loading model", e)
    );
  }, [stlFile, generateSlicesAndSlots, createVisualization, checkModelBBoxConstraints]);

  // 初始化场景与模型
  useEffect(() => {
    const cleanup = initializeThreeScene(
      containerRef,
      sceneRef,
      rendererRef,
      cameraRef,
      controlsRef
    );
    initHelpers();
    loadModel();
    return cleanup;
  }, [initHelpers, loadModel]);

  // 参数变化时重建
  useEffect(() => {
    if (!modelRef.current) return;
    const t = setTimeout(() => generateSlicesAndSlots(), 150);
    return () => clearTimeout(t);
  }, [cutNumber, materialThickness, generateSlicesAndSlots]);

  return (
    <div className="container">
      
{/* 预警气泡 */}
{constraintWarnings.length > 0 && (
  <div style={{
    position: "fixed", top: 20, left: "50%", transform: "translateX(-50%)",
    zIndex: 1000, display: "flex", flexDirection: "column", gap: 8
  }}>
    {constraintWarnings.map((w, i) => (
      <div key={i} style={{
        backgroundColor: "#ff9800", color: "#fff", padding: "12px 16px",
        borderRadius: 6, boxShadow: "0 2px 10px rgba(0,0,0,0.2)", position: "relative"
      }}>
        <div style={{ display: "flex", alignItems: "center", marginBottom: 6 }}>
          <span style={{ fontSize: 16, marginRight: 8 }}>⚠️</span>
          <strong>Model Size Exceeds {w.cutter}</strong>
        </div>
        <div style={{ fontSize: 13 }}>
          <div>Longest side: {w.currentSize.width} mm</div>
          <div>Bed shortest side: {w.maxSize.width} mm</div>
        </div>
        <button
          onClick={() => setConstraintWarnings(prev => prev.filter((_, idx) => idx !== i))}
          style={{ 
            position: "absolute", top: 8, right: 8, background: "none", 
            border: "none", color: "#fff", fontSize: 16, cursor: "pointer" 
          }}
        >×</button>
      </div>
    ))}
  </div>
)}
      <div ref={containerRef} className="canvas-container" />

      <TutorialWindow
        visible={tutorialVisible}
        onToggleVisibility={() => setTutorialVisible((prev) => !prev)}
        tutorialContent={paperMacheTutorial}
      />

      <button
        className="panel-toggle-button"
        onClick={() => setShowControlPanel(!showControlPanel)}
      >
        {showControlPanel ? "⟨ Hide Panel" : "⟩ Show Panel"}
      </button>

      {showControlPanel && (
        <div className="controls-panel">
          <div className="panel-title-row">
  <div className="panel-title">Paper Mache Shaping</div>
  {slowFabrication && (
    <span
      className="iteration-badge badge--warn"
      title="Layered cutting + paste mixing + multi-day cure & sanding cycles"
    >
      Long manual process
    </span>
  )}
</div>


          <div className="panel-section">
            <div className="section-header">
              <h4>Workflow Description</h4>
            </div>
            <div className="section-content">
              <p>
                This workflow slices your 3D model and creates layered paper mache shaping guides
                using orthogonal cutting planes. The layers can be printed and used to construct
                a sculptural armature or mold.
              </p>
              <p style={{ fontStyle: "italic" }}>
                Recommended for organic forms that need shaping over layered guides like foam or
                pulp bases.
              </p>
            </div>
          </div>

          <div className="panel-section">
  <div className="section-header"><h4>Time & Effort</h4></div>
  <div className="section-content">
    <ul style={{ marginLeft: 16, lineHeight: 1.5, fontSize: 13 }}>
      <li><strong>Template cutting & assembly</strong>: print or laser-cut slice guides; label and dry-fit.</li>
      <li><strong>Armature prep</strong>.</li>
      <li><strong>First mache layer</strong>: mix paste (PVA or flour mix), lay paper strips; focus on adhesion.</li>
      <li><strong>Cure cycle</strong>: allow full dry (often 12–24h per layer depending on humidity).</li>
      <li><strong>Build layers</strong>: 2–4 additional passes for strength and smoothing; each needs curing.</li>
      <li><strong>Shaping & finish</strong>: light sanding, patch with pulp or gesso; optional primer/paint/seal.</li>
      <li><strong>Safety & cleanup</strong>: ventilation for PVA/ammonia cleaners; manage wet waste properly.</li>
    </ul>

  </div>
</div>


          <div className="input-group">
            <label>Number of Slices</label>
            <div className="value-display">
              <input
                type="range"
                className="slider"
                min="2"
                max="12"
                value={cutNumber}
                onChange={(e) => setCutNumber(parseInt(e.target.value))}
              />
              <div className="value">{cutNumber}</div>
            </div>
          </div>

          <div className="input-group">
            <label>Material Thickness (mm)</label>
              <div className="value-display">
                <input
                  type="range"
                  min={thicknessMin}
                  max={thicknessMax}
                  step={thicknessStep}
                  value={materialThickness}
                  onChange={(e) => setMaterialThickness(Number(e.target.value))}
                />
                <div className="value">{materialThickness} mm</div>
              </div>
          </div>

          <div className="buttons-container">
          {/*   <button
              onClick={handleDownloadSVG}
              className="action-button"
              disabled={!svgReady}
            >
              DOWNLOAD SVG FILES
            </button> */}

            <button
              onClick={() => setTutorialVisible((prev) => !prev)}
              className="action-button"
            >
              {tutorialVisible
                ? "HIDE INTERACTIVE GUIDANCE"
                : "SHOW INTERACTIVE GUIDANCE"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaperMacheShaping;
